import { HoverLink } from '@campfire/hover-link';
import { Box, Typography, Button, Card, CardContent, CardActions, CircularProgress } from '@material-ui/core';
import React from 'react';
import { useHistory } from 'react-router';
import { useCampfireTheme } from '../../theme/useCampfireTheme';
import { PublicAppBar } from '../public-shell/public-app-bar/PublicAppBar';
import { useOrgInfo } from '../auth/use-org-info/use-org-info';
import { useSession } from '../auth/useSession';

// Lazy import PricingPlans to avoid loading it unless needed
const PricingPlans = React.lazy(() =>
  import(
    '../../screens/admin/admin-console/admin-console-content-pages/admin-console-billing/PricingPlans'
  ).then((module) => ({ default: module.PricingPlans }))
);

export const TenantDeactivatedShell = () => {
  const { theme } = useCampfireTheme();
  const history = useHistory();
  const orgInfo = useOrgInfo();
  const { user } = useSession();

  const isTrialAccount = orgInfo?.inTrial ?? false;
  const title = isTrialAccount ? 'Trial Ended' : 'Subscription Ended';

  // Check if user is authenticated and is admin
  let isAdmin = false;

  // Access userIdentityService directly from user object (not through useUser hook)
  if (user && user.userIdentityService) {
    isAdmin = user.userIdentityService.privLevel === 'admin';

    // Debug logging
    console.log('User debug info:', {
      hasUser: !!user,
      privLevel: user.userIdentityService.privLevel,
      isAdmin: isAdmin,
    });
  }

  const handleResubscribe = () => {
    if (!user) {
      // Redirect to sign-in page, which will bring them back here after auth
      history.push('/sign-in');
    }
    // If user is admin, they'll see PricingPlans embedded directly
    // No need to redirect since we're in deactivated shell
  };

  const openIntercom = () => {
    if ((window as any).Intercom) {
      (window as any).Intercom('show');
    }
  };

  // Determine what content to show
  const getCardContent = () => {
    if (user && isAdmin) {
      // Authenticated admin - show embedded billing/pricing
      return renderAdminBillingContent();
    }
    if (user && !isAdmin) {
      // Authenticated non-admin - show contact message
      return renderNonAdminContent();
    }
    // Default content with resubscribe option
    return renderDefaultContent();
  };

  const renderDefaultContent = () => (
    <CardContent style={{ padding: '24px' }}>
      <Typography
        variant='h4'
        style={{
          fontWeight: 700,
          fontSize: '24px',
          marginBottom: '16px',
          color: '#343434',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant='body1'
        style={{
          fontWeight: 400,
          fontSize: '14px',
          color: theme.palette.text.secondary,
          marginBottom: '16px',
        }}
      >
        Your account is currently inactive.
      </Typography>
      {isTrialAccount ? (
        <Typography
          variant='body1'
          style={{
            fontWeight: 400,
            fontSize: '14px',
            color: theme.palette.text.secondary,
          }}
        >
          If you&apos;d like to resubscribe, click the button below.
        </Typography>
      ) : (
        <>
          <Typography
            variant='body1'
            style={{
              fontWeight: 400,
              fontSize: '14px',
              color: theme.palette.text.secondary,
              display: 'inline',
            }}
          >
            If your trial has ended and you would like to get started on your full account,&nbsp;
          </Typography>
          <HoverLink external to='https://volaby.org/contact' rel='noopener' target='_blank' hoverColor='primary'>
            <Typography
              variant='body1'
              style={{
                fontWeight: 400,
                fontSize: '14px',
                display: 'inline',
                textDecoration: 'underline',
                textDecorationThickness: '1px',
              }}
            >
              please click here to get started.
            </Typography>
          </HoverLink>
        </>
      )}
      <br />
      <Typography
        variant='body1'
        style={{
          fontWeight: 400,
          fontSize: '14px',
          color: theme.palette.text.secondary,
          display: 'inline',
          marginTop: '8px',
        }}
      >
        If this doesn&apos;t seem right or you would like to speak with one of our team,&nbsp;
      </Typography>
      <Typography
        variant='body1'
        onClick={openIntercom}
        style={{
          fontWeight: 400,
          fontSize: '14px',
          display: 'inline',
          textDecoration: 'underline',
          textDecorationThickness: '1px',
          cursor: 'pointer',
          color: theme.palette.primary.main,
        }}
      >
        click here
      </Typography>
    </CardContent>
  );

  const renderNonAdminContent = () => (
    <CardContent style={{ padding: '24px' }}>
      <Typography
        variant='h4'
        style={{
          fontWeight: 700,
          fontSize: '24px',
          marginBottom: '16px',
          color: '#343434',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant='body1'
        style={{
          fontWeight: 400,
          fontSize: '14px',
          color: theme.palette.text.secondary,
          marginBottom: '16px',
        }}
      >
        Your account is currently inactive. Please contact your organisation administrator to reactivate your
        subscription.
      </Typography>
    </CardContent>
  );

  const renderAdminBillingContent = () => (
    <Box>
      <CardContent style={{ padding: '24px' }}>
        <Typography
          variant='h4'
          style={{
            fontWeight: 700,
            fontSize: '24px',
            marginBottom: '16px',
            color: '#343434',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body1'
          style={{
            fontWeight: 400,
            fontSize: '14px',
            color: theme.palette.text.secondary,
            marginBottom: '16px',
          }}
        >
          Your account is currently inactive. As an administrator, you can reactivate your subscription by selecting a
          plan below.
        </Typography>
        <Typography
          variant='body1'
          style={{
            fontWeight: 400,
            fontSize: '14px',
            color: theme.palette.text.secondary,
            marginBottom: '24px',
          }}
        >
          Choose the plan that best fits your organisation&apos;s needs and click Select to begin the reactivation
          process.
        </Typography>
      </CardContent>

      <React.Suspense
        fallback={
          <CardContent style={{ padding: '24px', textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant='body1' style={{ marginTop: '16px' }}>
              Loading pricing options...
            </Typography>
          </CardContent>
        }
      >
        <Box style={{ padding: '0 24px 24px 24px' }}>
          <PricingPlans cardOnly />
        </Box>
      </React.Suspense>
    </Box>
  );

  return (
    <>
      <PublicAppBar />
      <Box
        width={1}
        marginTop={`${theme.appShell.menu.height}px`}
        minHeight={`calc(100vh - ${theme.appShell.menu.height}px)`}
        display='flex'
        justifyContent='center'
        alignItems={user && isAdmin ? 'flex-start' : 'center'}
        paddingY={user && isAdmin ? '40px' : '0px'}
      >
        <Card
          style={{
            borderRadius: 12,
            backgroundColor: 'white',
            padding: '48px 40px',
            maxWidth: 800,
            width: '90%',
            border: 'none',
            boxShadow: 'rgba(35, 63, 77, 0.1) 0px 8px 24px 8px',
          }}
        >
          {getCardContent()}
          {/* Show buttons based on user state */}
          {!user && (
            <CardActions style={{ padding: '16px 0 0 0', justifyContent: 'space-between' }}>
              <Button
                onClick={openIntercom}
                variant='outlined'
                style={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Contact Support
              </Button>
              {isTrialAccount && (
                <Button
                  onClick={handleResubscribe}
                  variant='contained'
                  color='primary'
                  style={{
                    textTransform: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                >
                  Resubscribe
                </Button>
              )}
            </CardActions>
          )}
          {/* Non-admin users get contact support button */}
          {user && !isAdmin && (
            <CardActions style={{ padding: '16px 0 0 0', justifyContent: 'center' }}>
              <Button
                onClick={openIntercom}
                variant='outlined'
                style={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Contact Support
              </Button>
            </CardActions>
          )}
        </Card>
      </Box>
    </>
  );
};
